/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { NoteAPI } from "../../api/Note";
import { NoteCategoriesAPI } from "../../api/NoteCategory";
import { UserAPI } from "../../api/User";
import { ContactsAtom } from "../../atoms/ContactsAtom";
import { NoteCategoriesAtom } from "../../atoms/NotesAtom";
import { UserAtom } from "../../atoms/UserAtom";
import { ContactStatus, IContact } from "../../types/Contact";
import { INoteType } from "../../types/NoteCategory";
import { localStorageGetItem } from "../../utils/LocalStorage";
import Loading from "../Loading";
import { useModal } from "../Modal/Modal";
import AutoCompleteNoteCategory, {
  INoteCategoryOption,
} from "../Notes/AutoCompleteNoteCategory";
import StyledButton from "../StyledButton";
import StyledCheckBox from "../StyledCheckBox";
import StyledTextArea from "../StyledTextArea";

const Wrapper = styled.div`
  width: 500px;
  height: 80%;
  overflow-y: auto;
  margin: 15px -25px 25px -25px;
  padding: 30px 30px 0px 30px;
  border-top: 1px solid #eceeee;
  border-bottom: 1px solid #eceeee;
`;

const BlockOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const BlockOptionItem = styled.div`
  display: flex;
  grid-gap: 15px;
  margin-left: -4px;
  margin-bottom: 12px;
`;

const BottomDiv = styled.div`
  display: flex;
  justify-content: flex-end;
  grid-gap: 15px;
`;

const DetailDiv = styled.span`
  color: #888888;
  margin-left: 5px;
`;

const InputWrapper = styled.div`
  margin: 30px 0;
`;

const Title = styled.div`
  color: #2f2f2f;
  font-weight: 600;
`;

interface IBlockOptions {
  deviceIp: boolean;
  deviceId: boolean;
  phone: boolean;
  email: boolean;
}
interface Props {
  contact: IContact;
}

export default function UnblockContactModal(props: Props) {
  const { contact } = props;
  const [loading, setLoading] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(true);
  const modal = useModal();
  const user = useRecoilValue(UserAtom);
  const setContacts = useSetRecoilState(ContactsAtom);
  const [noteText, setNoteText] = useState("");
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const lastBlockNoteCategoryName = localStorageGetItem(
    "LAST_NOTE_CATEGORY"
  ) as string;
  const [categoryInputValue, setCategoryInputValue] = useState(
    lastBlockNoteCategoryName || ""
  );

  const setCategories = useSetRecoilState(NoteCategoriesAtom);
  const [categorySelected, setCategorySelected] = useState<{
    id: string;
    name: string;
    type: string;
  }>({
    id: "",
    name: "",
    type: "",
  });
  const [categorySuggestions, setCategorySuggestions] = useState<
    { id: string; name: string; type: INoteType }[]
  >([]);

  const [blockOptions, setBlockOptions] = useState<IBlockOptions>({
    deviceIp: !!contact?.lastIp,
    deviceId: !!contact?.deviceIds?.length,
    phone: !!contact.phone,
    email: true,
  });

  useEffect(() => {
    if (textAreaRef.current) textAreaRef.current.focus();
  }, []);

  async function getNoteCategories() {
    try {
      const categoriesResponse = await NoteCategoriesAPI.readAll();

      if (categoriesResponse) {
        setCategories(categoriesResponse);

        const newSuggestions = categoriesResponse.map((c) => ({
          id: c._id?.toString() || "",
          name: c.name,
          type: c.type,
        }));

        setCategorySuggestions(newSuggestions);

        if (lastBlockNoteCategoryName) {
          const foundNoteCategory = categoriesResponse.find(
            (category) => category.name === lastBlockNoteCategoryName
          );
          if (foundNoteCategory) {
            setCategorySelected({
              id: foundNoteCategory._id?.toString() || "",
              name: foundNoteCategory.name,
              type: foundNoteCategory.type,
            });
          }
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingNotes(false);
    }
  }

  useEffect(() => {
    getNoteCategories();
  }, []);

  function checkUnblockOptions() {
    let valid = false;

    Object.values(blockOptions).map((v) => {
      if (v === true) valid = true;
    });

    return !valid;
  }

  async function handleCategoryValueSelect(option: INoteCategoryOption) {
    if (option) {
      setCategorySelected(option);
      setCategoryInputValue(option.name);
    }
  }

  async function createNote() {
    try {
      await NoteAPI.createUserNote({
        noteCategoryId: categorySelected.id,
        note: noteText,
        contactId: contact._id,
      });
    } catch (e) {
      toast("Error on create note", { type: "warning" });
    } finally {
      modal.popModal();
    }
  }

  async function handleUnblockUser() {
    try {
      await UserAPI.removeEmailRestrictions({
        emailsBlocked: [contact.email],
        ...(blockOptions.phone && { phoneNumbersBlocked: [contact.phone] }),
        ...(blockOptions.deviceId && { deviceIdsBlocked: contact.deviceIds }),
        ...(blockOptions.deviceIp && { deviceIpsBlocked: [contact.lastIp] }),
      });

      setContacts((contactsFromAtom) =>
        contactsFromAtom.map((c) => {
          if (contact._id === c._id) {
            return { ...c, status: ContactStatus.NOT_CONFIRMED };
          }
          return c;
        })
      );

      if (noteText && categoryInputValue) createNote();

      modal.popModal();
      toast("Success", { type: "success" });
    } catch (err) {
      toast("Unexpected Error", { type: "error" });
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Wrapper>
        {loadingNotes ? (
          <div style={{ display: "flex" }}>
            <Loading />
          </div>
        ) : (
          <BlockOptionsContainer>
            {contact?.lastIp &&
              user.emailRestrictions.deviceIpsBlocked.includes(
                contact?.lastIp
              ) && (
                <BlockOptionItem>
                  <StyledCheckBox
                    handleCheck={() =>
                      setBlockOptions({
                        ...blockOptions,
                        deviceIp: !blockOptions.deviceIp,
                      })
                    }
                    check={blockOptions.deviceIp}
                  />
                  <div>
                    Unblock Device Ip
                    <DetailDiv>({contact.lastIp}</DetailDiv>
                  </div>
                </BlockOptionItem>
              )}
            {contact.deviceIds?.length &&
              user.emailRestrictions.deviceIdsBlocked.includes(
                contact?.deviceIds[contact?.deviceIds.length - 1]
              ) && (
                <BlockOptionItem>
                  <StyledCheckBox
                    handleCheck={() =>
                      setBlockOptions({
                        ...blockOptions,
                        deviceId: !blockOptions.deviceId,
                      })
                    }
                    check={blockOptions.deviceId}
                  />
                  <div>
                    Unblock Device ID
                    <DetailDiv>
                      (
                      {contact.deviceIds.length > 1
                        ? "Many Entries"
                        : contact.deviceIds[0]}
                      )
                    </DetailDiv>
                  </div>
                </BlockOptionItem>
              )}
            {contact.phone && (
              <BlockOptionItem>
                <StyledCheckBox
                  handleCheck={() =>
                    setBlockOptions({
                      ...blockOptions,
                      phone: !blockOptions.phone,
                    })
                  }
                  check={blockOptions.phone}
                />
                <div>
                  Unblock Phone
                  <DetailDiv>({contact.phone})</DetailDiv>
                </div>
              </BlockOptionItem>
            )}
            <BlockOptionItem>
              <StyledCheckBox
                handleCheck={() =>
                  setBlockOptions({
                    ...blockOptions,
                    email: !blockOptions.email,
                  })
                }
                check={blockOptions.email}
              />
              <div>
                Unblock Email
                <DetailDiv>({contact.email})</DetailDiv>
              </div>
            </BlockOptionItem>

            <InputWrapper>
              <Title>Note</Title>
              <StyledTextArea
                ref={textAreaRef}
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Enter the note"
                css={css`
                  margin-top: 5px;
                `}
              />

              <Title
                css={css`
                  margin: 20px 0px 5px;
                `}
              >
                Note Category
              </Title>
              <AutoCompleteNoteCategory
                selectedValue={categoryInputValue}
                onValueSelect={(option) => handleCategoryValueSelect(option)}
                onEnter={(option) => handleCategoryValueSelect(option)}
                onValueChange={setCategoryInputValue}
                placeholder="Type or select a category for this note"
                suggestions={categorySuggestions}
                onCancel={() => {
                  setCategoryInputValue("");
                }}
                optionIsSelected={(() => {
                  if (!categorySelected) return false;
                  if (categorySelected.name !== categoryInputValue)
                    return false;
                  else return true;
                })()}
              />
            </InputWrapper>
          </BlockOptionsContainer>
        )}
      </Wrapper>
      <BottomDiv>
        <StyledButton onClick={() => modal.popModal()} inverted>
          Cancel
        </StyledButton>
        <StyledButton
          isLoading={loading}
          disable={checkUnblockOptions()}
          onClick={handleUnblockUser}
        >
          Unblock
        </StyledButton>
      </BottomDiv>
    </>
  );
}
